/* Refresh cached storefront files in the background, retaining the current page and customer data. */
(() => {
  if (window.Shopify?.designMode || window.Shopify?.theme?.role !== "main")
    return;
  const pageUrl = new URL(window.location.href);
  if (pageUrl.searchParams.has("preview_theme_id")) return;
  const endpoint = new URL(
    document.currentScript?.dataset.endpoint ||
      "/apps/framewerk/storefront-cache",
    pageUrl.origin,
  );
  if (endpoint.origin !== pageUrl.origin) return;
  // Clean up legacy refresh URLs without navigating again.
  if (pageUrl.searchParams.has("_fw_refresh")) {
    pageUrl.searchParams.delete("_fw_refresh");
    window.history.replaceState(window.history.state, "", pageUrl.href);
  }
  const key = "framewerk:site-cache:v1";
  const attemptKey = "framewerk:site-cache-attempt:v1";
  let started = false;
  let interacted = false;
  let storage;
  for (const name of ["localStorage", "sessionStorage"]) {
    try {
      const candidate = window[name];
      candidate.setItem(key + ":probe", "1");
      if (candidate.getItem(key + ":probe") !== "1") continue;
      candidate.removeItem(key + ":probe");
      storage = candidate;
      break;
    } catch {
      /* Storage can be blocked in private browsing. */
    }
  }
  // Only refresh when the completed-release marker can persist.
  if (!storage) return;
  for (const event of ["pointerdown", "keydown", "input", "change", "submit"]) {
    document.addEventListener(
      event,
      () => {
        interacted = true;
      },
      { capture: true, once: true },
    );
  }
  function safeToRefresh() {
    return (
      !interacted &&
      document.visibilityState !== "hidden" &&
      !/(?:^|\/)(?:cart|checkout|checkouts|account|orders|apps)(?:\/|$)/i.test(
        pageUrl.pathname,
      ) &&
      !document.querySelector(
        'input:focus, textarea:focus, select:focus, [contenteditable="true"]:focus',
      )
    );
  }
  async function refreshSharedFiles(html, signal) {
    const freshDocument = new DOMParser().parseFromString(html, "text/html");
    const urls = new Set();
    for (const node of freshDocument.querySelectorAll(
      'script[src], link[rel="stylesheet"][href], link[rel="modulepreload"][href]',
    )) {
      const url = new URL(
        node.getAttribute("src") || node.getAttribute("href"),
        pageUrl,
      );
      // Only this storefront's CDN files, never apps or third-party trackers.
      if (url.origin === pageUrl.origin && url.pathname.startsWith("/cdn/"))
        urls.add(url.href);
    }
    const queue = [...urls].slice(0, 64);
    await Promise.all(
      Array.from({ length: Math.min(4, queue.length) }, async () => {
        while (queue.length && !signal.aborted) {
          const url = queue.shift();
          try {
            const response = await fetch(url, {
              cache: "reload",
              credentials: "same-origin",
              signal,
            });
            // Consume the response so the browser can replace its cached file.
            if (response.ok) await response.arrayBuffer();
          } catch {
            /* The fresh page still loads versioned files normally. */
          }
        }
      }),
    );
  }
  async function run() {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 15000);
    try {
      const response = await fetch(endpoint, {
        cache: "no-store",
        credentials: "same-origin",
        signal: controller.signal,
      });
      if (!response.ok || new URL(response.url).origin !== endpoint.origin)
        return;
      const policy = await response.json();
      if (
        !policy.enabled ||
        !/^[a-zA-Z0-9._-]{1,80}$/.test(policy.release || "")
      )
        return;
      if (storage.getItem(key) === policy.release) {
        document.documentElement.dataset.framewerkStorefrontRelease =
          policy.release;
        return;
      }
      if (!safeToRefresh()) return;
      let previous;
      try {
        previous = JSON.parse(storage.getItem(attemptKey));
      } catch {
        /* Ignore corrupt markers. */
      }
      if (
        previous?.release === policy.release &&
        Date.now() - previous.at < 60000
      )
        return;
      const attempt = JSON.stringify({
        release: policy.release,
        at: Date.now(),
      });
      storage.setItem(attemptKey, attempt);
      if (storage.getItem(attemptKey) !== attempt) return;

      const freshUrl = new URL(pageUrl);
      freshUrl.hash = "";
      const fresh = await fetch(freshUrl, {
        cache: "reload",
        credentials: "same-origin",
        signal: controller.signal,
      });
      if (
        !fresh.ok ||
        new URL(fresh.url).origin !== pageUrl.origin ||
        !fresh.headers.get("Content-Type")?.includes("text/html")
      )
        return;
      await refreshSharedFiles(await fresh.text(), controller.signal);
      if (controller.signal.aborted) return;
      if (!safeToRefresh()) {
        storage.removeItem(attemptKey);
        return;
      }
      for (const cacheKey of [
        "framewerk:website-settings:v1",
        "framewerk:vehicle-catalogue:v1",
      ]) {
        try {
          window.sessionStorage.removeItem(cacheKey);
        } catch {
          /* Keep all customer data. */
        }
      }
      storage.setItem(key, policy.release);
      if (storage.getItem(key) !== policy.release) return;
      // Cached files are ready for the next normal navigation. Never reload
      // the visible page: that replays loaders and interrupts the visit.
      storage.removeItem(attemptKey);
      document.documentElement.dataset.framewerkStorefrontRelease = policy.release;
    } catch {
      /* A failed refresh must never prevent shopping. */
    } finally {
      window.clearTimeout(timeout);
    }
  }
  function start() {
    if (started || document.visibilityState === "hidden") return;
    started = true;
    if (window.navigator.locks?.request) {
      void window.navigator.locks
        .request(key, { ifAvailable: true }, (lock) =>
          lock ? run() : undefined,
        )
        .catch(() => {});
    } else {
      void run();
    }
  }
  if (document.readyState === "loading")
    window.addEventListener("DOMContentLoaded", start, { once: true });
  else start();
  document.addEventListener("visibilitychange", start);
  window.addEventListener("pageshow", (event) => {
    if (!event.persisted) return;
    interacted = true; // History restores can contain unsent forms.
    started = false;
    start();
  });
})();
